import type {
  GetCampaignCreatorsCountInput,
  ListCampaignCreatorsInput,
  ListCampaignPostsInput,
  ListCampaignsInput,
} from './generated/api';

export type CampaignListRequest = NonNullable<ListCampaignsInput['query']>;
export type InfiniteCampaignListRequest = Omit<CampaignListRequest, 'page'>;
export type CampaignCreatorListRequest = NonNullable<ListCampaignCreatorsInput['query']>;
export type CampaignCreatorCountRequest = NonNullable<GetCampaignCreatorsCountInput['query']>;
export type CampaignPostListRequest = NonNullable<ListCampaignPostsInput['query']>;

export const campaignQueryKeys = {
  all: (workspaceId: string) => [workspaceId, 'campaign'] as const,
  lists: (workspaceId: string, params: CampaignListRequest) =>
    [
      workspaceId,
      'campaign',
      'lists',
      params.sort ?? null,
      params.status ?? null,
      params.limit,
      params.page,
    ] as const,
  infiniteLists: (workspaceId: string, params?: InfiniteCampaignListRequest) =>
    params == null
      ? ([workspaceId, 'campaign', 'lists', 'infinite'] as const)
      : ([
          workspaceId,
          'campaign',
          'lists',
          'infinite',
          params.name ?? null,
          params.sort ?? null,
          params.status ?? null,
          params.limit,
        ] as const),
  slot: () => ['campaign', 'slot'] as const,
  nameAvailability: (workspaceId: string, name: string) =>
    [workspaceId, 'campaign', 'name-availability', name] as const,
  detail: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'detail', campaignId] as const,
  analysisList: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'analyses', campaignId, 'list'] as const,
  analysisPreflight: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'analyses', campaignId, 'preflight'] as const,
  analysisStatus: (workspaceId: string, campaignId: string, runId: string) =>
    [workspaceId, 'campaign', 'analyses', campaignId, runId, 'status'] as const,
  analysisResult: (workspaceId: string, campaignId: string, runId: string) =>
    [workspaceId, 'campaign', 'analyses', campaignId, runId, 'result'] as const,
  overview: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'overview', campaignId] as const,
  metrics: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'metrics', campaignId] as const,
  creatorsRoot: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'creators', campaignId] as const,
  creators: (
    workspaceId: string,
    campaignId: string,
    pending: boolean,
    request?: CampaignCreatorListRequest,
  ) => [workspaceId, 'campaign', 'creators', campaignId, pending, request] as const,
  creatorCountsRoot: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'creator-counts', campaignId] as const,
  creatorCount: (
    workspaceId: string,
    campaignId: string,
    pending: boolean,
    request?: CampaignCreatorCountRequest,
  ) => [workspaceId, 'campaign', 'creator-counts', campaignId, pending, request] as const,
  postsRoot: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'posts', campaignId] as const,
  posts: (
    workspaceId: string,
    campaignId: string,
    recent: boolean,
    request?: CampaignPostListRequest,
  ) =>
    [
      workspaceId,
      'campaign',
      'posts',
      campaignId,
      recent,
      'sort',
      request?.sort ?? null,
      'page',
      request?.page ?? 1,
      'limit',
      request?.limit ?? null,
    ] as const,
  postCountsRoot: (workspaceId: string, campaignId: string) =>
    [workspaceId, 'campaign', 'post-counts', campaignId] as const,
  postCount: (workspaceId: string, campaignId: string, recent: boolean) =>
    [workspaceId, 'campaign', 'post-counts', campaignId, recent] as const,
};
